<template>
  <div class="carousel-wrapper" ref="wrapper">
    <div 
      class="carousel-track"
      ref="track"
      :style="{
        transform: `translate3d(${currentPosition}px, 0, 0)`,
        transition: isDragging || isAnimating || isScrolling ? 'none' : `transform ${transitionSpeed}ms linear`,
        willChange: 'transform'
      }"
      @mousedown="startDrag"
      @touchstart="handleTouchStart"
      @wheel="handleWheel"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <!-- 第一组图片 -->
      <div 
        v-for="(image, index) in displayImages" 
        :key="`image-${index}`"
        class="carousel-item"
        :style="{ 
          height: `${fixedHeight}px`,
          width: imageDimensions[index] ? `${imageDimensions[index].displayWidth}px` : '200px'
        }"
        @click="handleImageClick(image)"
      >
        <img 
          :src="image.src" 
          :alt="image.alt"
          :style="{ 
            height: `${fixedHeight}px`,
            width: imageDimensions[index] ? `${imageDimensions[index].displayWidth}px` : '200px',
            opacity: imageDimensions[index] ? 1 : 0
          }"
          @load="handleImageLoad(index, $event)"
        />
        <!-- <div v-if="showTitles" class="image-overlay">
          <div class="image-title">{{ image.title }}</div>
          <div class="image-size">{{ imageDimensions[index] ? `${imageDimensions[index].width}×${imageDimensions[index].height}` : '' }}</div>
        </div> -->
      </div>
      
      <!-- 第二组图片（用于无缝滚动） -->
      <div 
        v-for="(image, index) in displayImages" 
        :key="`image-duplicate-${index}`"
        class="carousel-item"
        :style="{ 
          height: `${fixedHeight}px`,
          width: imageDimensions[index] ? `${imageDimensions[index].displayWidth}px` : '200px'
        }"
        @click="handleImageClick(image)"
      >
        <img 
          :src="image.src" 
          :alt="image.alt"
          :style="{ 
            height: `${fixedHeight}px`,
            width: imageDimensions[index] ? `${imageDimensions[index].displayWidth}px` : '200px'
          }"
        />
        <!-- <div v-if="showTitles" class="image-overlay">
          <div class="image-title">{{ image.title }}</div>
          <div class="image-size">{{ imageDimensions[index] ? `${imageDimensions[index].width}×${imageDimensions[index].height}` : '' }}</div>
        </div> -->
      </div>
    </div>
    
    <!-- 移动端优化遮罩 -->
    <!-- <div v-if="isMobile && autoScroll && !isDragging" class="scroll-indicator">
      <div class="indicator-dot"></div>
      <div class="indicator-text">自动滚动中...</div>
    </div> -->
  </div>
</template>

<script>
export default {
  name: 'SmoothImageCarousel',
  props: {
    images: {
      type: Array,
      required: true,
      default: () => []
    },
    fixedHeight: {
      type: Number,
      default: 180
    },
    initialSpeed: {
      type: Number,
      default: 5
    },
    autoStart: {
      type: Boolean,
      default: true
    },
    gap: {
      type: Number,
      default: 15
    }
  },
  data() {
    return {
      // 滚动状态
      currentPosition: 0,
      autoScroll: this.autoStart,
      scrollSpeed: this.initialSpeed,
      
      // 交互状态
      isDragging: false,
      isAnimating: false,
      isScrolling: false,
      
      // 拖动相关
      dragStartX: 0,
      dragStartPosition: 0,
      dragVelocity: 0,
      lastDragTime: 0,
      lastDragX: 0,
      
      // 动画相关
      scrollAnimationId: null,
      lastScrollTime: 0,
      scrollDelta: 0,
      transitionSpeed: 300,
      
      // 图片数据
      displayImages: [],
      imageDimensions: [],
      itemWidths: [],
      avgItemWidth: 200,
      totalTrackWidth: 0,
      
      // 移动端优化
      isMobile: false,
      touchPoints: 0,
      
      // 性能优化
      performanceMode: false,
      useHardwareAcceleration: true,
      showTitles: true,
      showFPS: false,
      showPerformancePanel: false,
      
      // 性能监控
      fps: 60,
      fpsHistory: [],
      fpsCounter: 0,
      lastFpsTime: 0,
      scrollJitter: 0,
      frameTimes: [],
      memoryUsage: 0,
      
      // 防抖优化
      lastUpdateTime: 0,
      updateThreshold: 16, // ~60fps
      rafId: null
    };
  },
  computed: {
    scrollStep() {
      // 根据性能模式调整滚动步长
      const baseStep = 1.5;
      if (this.performanceMode) {
        return baseStep * 1.2; // 性能模式下稍微加快
      }
      if (this.isMobile) {
        return baseStep * 0.8; // 移动端稍微减慢
      }
      return baseStep;
    }
  },
  watch: {
    images: {
      immediate: true,
      handler(newVal) {
        if (newVal.length > 0) {
          this.displayImages = [...newVal];
          this.imageDimensions = new Array(newVal.length).fill(null);
          this.itemWidths = new Array(newVal.length).fill(0);
        } else {
          this.displayImages = this.getDefaultImages();
          this.imageDimensions = new Array(this.displayImages.length).fill(null);
          this.itemWidths = new Array(this.displayImages.length).fill(0);
        }
        
        this.$nextTick(() => {
          this.calculateTrackWidth();
        });
      }
    },
    autoScroll(newVal) {
      if (newVal) {
        this.startSmoothScroll();
      } else {
        this.stopSmoothScroll();
      }
    },
    useHardwareAcceleration(newVal) {
      if (this.$refs.track) {
        if (newVal) {
          this.$refs.track.style.transform = `translate3d(${this.currentPosition}px, 0, 0)`;
        } else {
          this.$refs.track.style.transform = `translateX(${this.currentPosition}px)`;
        }
      }
    },
    showFPS(newVal) {
      if (newVal) {
        this.startFpsMonitor();
      } else {
        this.stopFpsMonitor();
      }
    }
  },
  mounted() {
    this.detectMobile();
    this.setupPerformanceMonitoring();
    
    if (this.autoScroll) {
      this.startSmoothScroll();
    }
    
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // 初始化FPS历史记录
    this.fpsHistory = new Array(30).fill(60);
  },
  beforeDestroy() {
    this.stopSmoothScroll();
    this.stopFpsMonitor();
    
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  },
  methods: {
    // 获取默认图片
    getDefaultImages() {
      return [
        { 
          src: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=800&h=600&fit=crop', 
          title: '宽幅山脉', 
          alt: '山脉'
        },
        { 
          src: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=600&h=800&fit=crop', 
          title: '竖幅星空', 
          alt: '星空'
        },
        { 
          src: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1000&h=600&fit=crop', 
          title: '宽屏湖景', 
          alt: '湖泊'
        },
        { 
          src: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=700&h=500&fit=crop', 
          title: '自然风光', 
          alt: '自然'
        }
      ];
    },
    
    // 检测移动设备
    detectMobile() {
      this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth <= 768;
    },
    
    // 设置性能监控
    setupPerformanceMonitoring() {
      this.lastFpsTime = performance.now();
      this.lastScrollTime = performance.now();
      
      // 定期更新内存使用情况（如果浏览器支持）
      if (performance.memory) {
        setInterval(() => {
          this.memoryUsage = Math.round(performance.memory.usedJSHeapSize / 1024 / 1024);
        }, 2000);
      }
    },
    
    // 开始FPS监控
    startFpsMonitor() {
      this.fpsCounter = 0;
      this.lastFpsTime = performance.now();
      
      const updateFps = () => {
        const now = performance.now();
        const elapsed = now - this.lastFpsTime;
        
        if (elapsed >= 1000) {
          this.fps = Math.round((this.fpsCounter * 1000) / elapsed);
          
          // 更新FPS历史记录
          this.fpsHistory.push(this.fps);
          if (this.fpsHistory.length > 30) {
            this.fpsHistory.shift();
          }
          
          this.fpsCounter = 0;
          this.lastFpsTime = now;
        }
        
        this.fpsCounter++;
        
        if (this.showFPS) {
          requestAnimationFrame(updateFps);
        }
      };
      
      requestAnimationFrame(updateFps);
    },
    
    // 停止FPS监控
    stopFpsMonitor() {
      this.showFPS = false;
    },
    
    // 计算抖动率
    calculateJitter() {
      if (this.frameTimes.length < 2) return 0;
      
      const differences = [];
      for (let i = 1; i < this.frameTimes.length; i++) {
        differences.push(Math.abs(this.frameTimes[i] - this.frameTimes[i-1]));
      }
      
      const avg = differences.reduce((a, b) => a + b, 0) / differences.length;
      const variance = differences.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / differences.length;
      
      return Math.round(Math.sqrt(variance) / avg * 100);
    },
    
    // 图片加载处理
    handleImageLoad(index, event) {
      const img = event.target;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      
      // 根据固定高度计算显示宽度
      const displayWidth = (naturalWidth / naturalHeight) * this.fixedHeight;
      
      this.$set(this.imageDimensions, index, {
        width: naturalWidth,
        height: naturalHeight,
        displayWidth: displayWidth
      });
      
      this.$set(this.itemWidths, index, displayWidth + this.gap);
      
      // 延迟计算，避免频繁重绘
      this.calculateTrackWidthDebounced();
    },
    
    // 防抖计算轨道宽度
    calculateTrackWidthDebounced: _.debounce(function() {
      this.calculateTrackWidth();
    }, 100),
    
    // 计算轨道宽度
    calculateTrackWidth() {
      const validWidths = this.itemWidths.filter(width => width > 0);
      if (validWidths.length === this.displayImages.length) {
        // 计算平均宽度
        this.avgItemWidth = validWidths.reduce((sum, width) => sum + width, 0) / validWidths.length;
        
        // 计算总轨道宽度（原始+复制）
        this.totalTrackWidth = validWidths.reduce((sum, width) => sum + width, 0) * 2;
      }
    },
    
    // 优化后的平滑滚动
    startSmoothScroll() {
      if (this.scrollAnimationId) {
        cancelAnimationFrame(this.scrollAnimationId);
      }
      
      this.isScrolling = true;
      this.lastScrollTime = performance.now();
      
      const animateScroll = (timestamp) => {
        if (!this.autoScroll || this.isDragging) {
          this.isScrolling = false;
          return;
        }
        
        // 计算时间差，实现帧率无关的动画
        const currentTime = timestamp || performance.now();
        const deltaTime = currentTime - this.lastScrollTime;
        
        // 限制最大时间差，防止页面隐藏后恢复时的跳跃
        const maxDelta = 1000 / 30; // 30fps的最小帧时间
        const safeDelta = Math.min(deltaTime, maxDelta);
        
        // 计算滚动距离（速度 * 时间）
        const scrollDistance = (this.scrollSpeed * 0.3) * (safeDelta / 16.67); // 16.67ms ≈ 60fps一帧
        
        // 更新位置
        this.currentPosition -= scrollDistance;
        
        // 记录帧时间用于抖动计算
        const frameTime = performance.now();
        this.frameTimes.push(frameTime);
        if (this.frameTimes.length > 60) {
          this.frameTimes.shift();
        }
        
        // 计算滚动抖动
        if (this.lastScrollTime > 0) {
          const actualDelta = currentTime - this.lastScrollTime;
          const expectedDelta = 1000 / 60; // 期望的60fps帧时间
          this.scrollJitter = Math.round(Math.abs(actualDelta - expectedDelta));
        }
        
        // 检查无缝滚动边界
        if (Math.abs(this.currentPosition) >= this.totalTrackWidth / 2) {
          // 使用更平滑的跳转方式
          this.currentPosition += this.totalTrackWidth / 2;
          
          // 在性能模式下，可以跳过一些计算
          if (!this.performanceMode) {
            this.$forceUpdate();
          }
        }
        
        this.lastScrollTime = currentTime;
        
        // 继续动画循环
        if (this.autoScroll && !this.isDragging) {
          this.scrollAnimationId = requestAnimationFrame(animateScroll);
        }
      };
      
      this.scrollAnimationId = requestAnimationFrame(animateScroll);
    },
    
    // 停止平滑滚动
    stopSmoothScroll() {
      if (this.scrollAnimationId) {
        cancelAnimationFrame(this.scrollAnimationId);
        this.scrollAnimationId = null;
      }
      this.isScrolling = false;
    },
    
    // 切换自动滚动
    toggleAutoScroll() {
      this.autoScroll = !this.autoScroll;
      if (this.autoScroll) {
        this.startSmoothScroll();
      } else {
        this.stopSmoothScroll();
      }
    },
    
    // 切换性能模式
    togglePerformanceMode() {
      this.performanceMode = !this.performanceMode;
      
      if (this.performanceMode) {
        // 性能模式下优化设置
        this.showTitles = false;
        this.transitionSpeed = 200;
      } else {
        // 正常模式恢复设置
        this.showTitles = true;
        this.transitionSpeed = 300;
      }
      
      // 重新开始滚动
      if (this.autoScroll) {
        this.stopSmoothScroll();
        this.$nextTick(() => {
          this.startSmoothScroll();
        });
      }
    },
    
    // 更新滚动间隔
    updateScrollInterval() {
      // 重新开始滚动以应用新速度
      if (this.autoScroll) {
        this.stopSmoothScroll();
        this.$nextTick(() => {
          this.startSmoothScroll();
        });
      }
    },
    
    // 触摸开始处理
    handleTouchStart(event) {
      event.preventDefault();
      this.startDrag(event);
    },
    
    // 开始拖动
    startDrag(event) {
      // 停止自动滚动
      this.stopSmoothScroll();
      
      this.isDragging = true;
      this.isAnimating = false;
      
      const clientX = event.type.includes('mouse') ? event.clientX : event.touches[0].clientX;
      this.dragStartX = clientX;
      this.dragStartPosition = this.currentPosition;
      this.lastDragX = clientX;
      this.lastDragTime = performance.now();
      
      // 添加事件监听器
      const options = { passive: false };
      window.addEventListener('mousemove', this.handleDrag, options);
      window.addEventListener('touchmove', this.handleDrag, options);
      window.addEventListener('mouseup', this.stopDrag);
      window.addEventListener('touchend', this.stopDrag);
      window.addEventListener('touchcancel', this.stopDrag);
    },
    
    // 处理拖动
    handleDrag(event) {
      if (!this.isDragging) return;
      
      event.preventDefault();
      
      const currentTime = performance.now();
      
      // 使用requestAnimationFrame进行节流
      if (currentTime - this.lastUpdateTime < this.updateThreshold) {
        return;
      }
      
      const clientX = event.type.includes('mouse') ? event.clientX : event.touches[0].clientX;
      const deltaX = clientX - this.dragStartX;
      
      // 更新位置
      this.currentPosition = this.dragStartPosition + deltaX;
      
      // 计算速度
      const timeDelta = currentTime - this.lastDragTime;
      if (timeDelta > 0) {
        const distanceDelta = clientX - this.lastDragX;
        this.dragVelocity = distanceDelta / timeDelta;
      }
      
      this.lastDragX = clientX;
      this.lastDragTime = currentTime;
      this.lastUpdateTime = currentTime;
    },
    
    // 停止拖动
    stopDrag() {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      
      // 移除事件监听器
      window.removeEventListener('mousemove', this.handleDrag);
      window.removeEventListener('touchmove', this.handleDrag);
      window.removeEventListener('mouseup', this.stopDrag);
      window.removeEventListener('touchend', this.stopDrag);
      window.removeEventListener('touchcancel', this.stopDrag);
      
      // 应用惯性效果
      if (Math.abs(this.dragVelocity) > 0.1) {
        this.applyInertia();
      } else {
        this.finishDrag();
      }
    },
    
    // 应用惯性效果
    applyInertia() {
      this.isAnimating = true;
      
      const startTime = performance.now();
      const initialVelocity = this.dragVelocity * 0.8; // 稍微减速
      const friction = 0.95; // 摩擦系数
      
      const animateInertia = (currentTime) => {
        if (!this.isAnimating) return;
        
        const elapsed = currentTime - startTime;
        
        if (elapsed < 1000 && Math.abs(initialVelocity) > 0.01) {
          // 指数衰减
          const decay = Math.pow(friction, elapsed / 16.67);
          const currentVelocity = initialVelocity * decay;
          
          this.currentPosition += currentVelocity * 16.67; // 基于60fps的固定时间步长
          
          // 继续动画
          requestAnimationFrame(animateInertia);
        } else {
          this.isAnimating = false;
          this.finishDrag();
        }
      };
      
      requestAnimationFrame(animateInertia);
    },
    
    // 完成拖动
    finishDrag() {
      // 调整位置到有效范围
      this.adjustPosition();
      
      // 如果自动滚动开启，重新开始
      if (this.autoScroll) {
        this.startSmoothScroll();
      }
    },
    
    // 调整位置
    adjustPosition() {
      const halfWidth = this.totalTrackWidth / 2;
      
      if (halfWidth > 0) {
        if (this.currentPosition > 0) {
          this.currentPosition -= halfWidth;
        } else if (Math.abs(this.currentPosition) >= halfWidth) {
          this.currentPosition += halfWidth;
        }
      }
    },
    
    // 处理滚轮
    handleWheel(event) {
      event.preventDefault();
      
      // 停止自动滚动
      this.stopSmoothScroll();
      
      // 滚轮滚动
      this.currentPosition += event.deltaY > 0 ? -30 : 30;
      this.adjustPosition();
      
      // 延迟重新开始自动滚动
      setTimeout(() => {
        if (this.autoScroll && !this.isDragging) {
          this.startSmoothScroll();
        }
      }, 1000);
    },
    
    // 鼠标进入
    handleMouseEnter() {
      // 暂停自动滚动
      this.stopSmoothScroll();
    },
    
    // 鼠标离开
    handleMouseLeave() {
      // 恢复自动滚动
      if (this.autoScroll && !this.isDragging && !this.isAnimating) {
        this.startSmoothScroll();
      }
    },
    
    // 重置位置
    resetPosition() {
      this.currentPosition = 0;
      this.stopSmoothScroll();
      
      if (this.autoScroll) {
        setTimeout(() => {
          this.startSmoothScroll();
        }, 500);
      }
    },
    
    // 处理图片点击
    handleImageClick(image) {
      this.$emit('image-click', image);
    },
    
    // 处理窗口大小变化
    handleResize() {
      this.detectMobile();
      this.calculateTrackWidth();
      this.adjustPosition();
    },
    
    // 处理页面可见性变化
    handleVisibilityChange() {
      if (document.hidden) {
        // 页面隐藏时停止动画
        this.stopSmoothScroll();
      } else if (this.autoScroll && !this.isDragging) {
        // 页面显示时恢复动画
        this.startSmoothScroll();
      }
    }
  }
};
</script>

<style scoped>
.smooth-carousel-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #1a1a2e;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  -webkit-tap-highlight-color: transparent;
  -webkit-font-smoothing: antialiased;
}

.carousel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  color: #fff;
  flex-wrap: wrap;
  gap: 15px;
}

.carousel-header h2 {
  margin: 0;
  font-weight: 600;
  color: #4cc9f0;
  font-size: 1.5rem;
}

.controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.controls button {
  padding: 8px 16px;
  background-color: #16213e;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 0.9rem;
  touch-action: manipulation;
  min-width: 70px;
  white-space: nowrap;
}

.controls button:hover {
  background-color: #0f3460;
  transform: translateY(-1px);
}

.controls button.active {
  background-color: #4cc9f0;
  color: #16213e;
}

.speed-control {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-size: 0.9rem;
}

.speed-control input[type="range"] {
  width: 70px;
  cursor: pointer;
  -webkit-appearance: none;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(to right, #4cc9f0, #4cc9f0);
  outline: none;
}

.speed-control input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.carousel-wrapper {
  overflow: hidden;
  position: relative;
  border-radius: 8px;
  padding: 15px 0;
  min-height: 220px;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.carousel-track {
  display: flex;
  align-items: center;
  cursor: grab;
  user-select: none;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
  perspective: 1000px;
  gap: 15px;
  padding: 0 10px;
}

.carousel-track:active {
  cursor: grabbing;
}

.carousel-item {
  flex: 0 0 auto;
  border-radius: 8px;
  overflow: hidden;
  background-color: #16213e;
  position: relative;
  transform: translateZ(0);
  will-change: transform;
  transition: transform 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.carousel-item:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  z-index: 10;
}

.carousel-item img {
  display: block;
  object-fit: cover;
  pointer-events: none;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  color: white;
  padding: 12px 10px;
  pointer-events: none;
  opacity: 0.9;
  backdrop-filter: blur(2px);
}

.image-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-size {
  font-size: 11px;
  opacity: 0.8;
}

/* 移动端滚动指示器 */
.scroll-indicator {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.6);
  padding: 6px 12px;
  border-radius: 20px;
  color: white;
  font-size: 12px;
  opacity: 0.7;
  animation: pulse 2s infinite;
  pointer-events: none;
}

@keyframes pulse {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 0.4; }
}

.indicator-dot {
  width: 6px;
  height: 6px;
  background: #4cc9f0;
  border-radius: 50%;
  animation: bounce 1.5s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(5px); }
}

.carousel-footer {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  color: #8a8da0;
  font-size: 14px;
}

.position-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
}

.position-indicator, .performance-info {
  background-color: #16213e;
  padding: 8px 15px;
  border-radius: 20px;
}

.fps-counter {
  color: #4cc9f0;
  font-weight: 600;
}

.optimization-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background-color: #16213e;
  padding: 15px;
  border-radius: 8px;
  min-width: 200px;
}

.control-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.control-item label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.control-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

/* 性能监控面板 */
.performance-panel {
  margin-top: 20px;
  background: rgba(22, 33, 62, 0.9);
  border-radius: 8px;
  padding: 15px;
  color: #fff;
  font-size: 12px;
}

.performance-panel h4 {
  margin: 0 0 10px 0;
  color: #4cc9f0;
  font-size: 14px;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
  margin-bottom: 15px;
}

.metric {
  background: rgba(0, 0, 0, 0.3);
  padding: 8px;
  border-radius: 4px;
  text-align: center;
}

.performance-chart {
  display: flex;
  align-items: flex-end;
  height: 60px;
  gap: 2px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 5px;
}

.chart-bar {
  flex: 1;
  background: #4cc9f0;
  border-radius: 2px;
  min-height: 2px;
  transition: height 0.3s ease;
}

.chart-bar.low {
  background: #ff6b6b;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .smooth-carousel-container {
    padding: 15px;
    border-radius: 8px;
  }
  
  .carousel-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .carousel-header h2 {
    text-align: center;
    font-size: 1.3rem;
  }
  
  .controls {
    justify-content: center;
  }
  
  .controls button {
    padding: 8px 12px;
    font-size: 0.85rem;
    min-width: 65px;
  }
  
  .carousel-wrapper {
    padding: 10px 0;
    border-radius: 6px;
  }
  
  .carousel-track {
    gap: 10px;
    padding: 0 8px;
  }
  
  .carousel-item {
    border-radius: 6px;
  }
  
  .carousel-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .position-info, .optimization-controls {
    min-width: 100%;
  }
  
  /* 移动端优化滚动性能 */
  .carousel-track {
    -webkit-overflow-scrolling: touch;
  }
  
  .carousel-item img {
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
  }
  
  /* 移动端减少阴影效果以提升性能 */
  .carousel-item {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  .carousel-item:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

@media (max-width: 480px) {
  .smooth-carousel-container {
    padding: 12px;
  }
  
  .carousel-header h2 {
    font-size: 1.2rem;
  }
  
  .controls {
    gap: 8px;
  }
  
  .controls button {
    padding: 6px 10px;
    font-size: 0.8rem;
    min-width: 60px;
  }
  
  .carousel-track {
    gap: 8px;
  }
  
  .image-overlay {
    padding: 8px;
  }
  
  .image-title {
    font-size: 12px;
  }
  
  .scroll-indicator {
    font-size: 11px;
    padding: 5px 10px;
  }
}

/* 减少动画效果的媒体查询 */
@media (prefers-reduced-motion: reduce) {
  .carousel-track {
    transition: none !important;
  }
  
  .carousel-item {
    transition: none !important;
  }
  
  .carousel-item:hover {
    transform: none !important;
  }
  
  .scroll-indicator {
    animation: none;
  }
  
  .indicator-dot {
    animation: none;
  }
}

/* 移动端触摸优化 */
@media (hover: none) and (pointer: coarse) {
  .carousel-item:hover {
    transform: none;
  }
  
  .carousel-track {
    cursor: default;
  }
  
  .carousel-track:active {
    cursor: default;
  }
}

/* 高性能模式优化 */
.performance-mode .carousel-item {
  box-shadow: none;
}

.performance-mode .carousel-item:hover {
  transform: none;
}

.performance-mode .image-overlay {
  display: none;
}
</style>